/**
 * Gestionnaire centralisé des favoris
 * Résout les problèmes de chargement et d'authentification
 */

import { useState } from 'react';
import { TOKEN_TYPES, isTokenValid, getToken, removeToken } from './tokenManager';
import * as api from '../services/api';

/**
 * Vérifie si l'utilisateur est authentifié pour les favoris
 */
export const isAuthenticatedForFavorites = () => {
  const token = getToken(TOKEN_TYPES.CLIENT);
  return token && isTokenValid(token);
};

/**
 * Charge les favoris avec gestion d'erreurs améliorée
 */
export const loadFavorites = async () => {
  try {
    // Vérifier l'authentification
    if (!isAuthenticatedForFavorites()) {
      throw new Error('Utilisateur non authentifié');
    }

    console.log('🔄 Chargement des favoris...');
    const favorites = await api.getUserFavorites();
    console.log('✅ Favoris chargés:', favorites);
    return favorites;
  } catch (error) {
    console.error('❌ Erreur lors du chargement des favoris:', error);
    
    // Gérer les erreurs spécifiques
    if (error.message === 'Token expiré ou invalide' || error.status === 401) {
      // Token expiré, nettoyer et demander reconnexion
      removeToken(TOKEN_TYPES.CLIENT);
      throw new Error('Session expirée. Veuillez vous reconnecter.');
    }
    
    if (error.status === 403) {
      throw new Error('Accès non autorisé aux favoris.');
    }
    
    if (error.status === 404) {
      // Aucun favori trouvé, retourner un tableau vide
      console.log('ℹ️ Aucun favori trouvé');
      return [];
    }
    
    if (error.status >= 500) {
      throw new Error('Erreur serveur. Veuillez réessayer plus tard.');
    }
    
    // Erreur réseau
    if (!navigator.onLine) {
      throw new Error('Connexion internet requise pour charger les favoris.');
    }
    
    // Erreur générique
    throw new Error('Impossible de charger les favoris. Veuillez réessayer.');
  }
};

/**
 * Ajoute un élément aux favoris
 */
export const addToFavorites = async (objectId, objectType) => {
  try {
    if (!isAuthenticatedForFavorites()) {
      throw new Error('Utilisateur non authentifié');
    }

    console.log(`➕ Ajout aux favoris: ${objectType} ${objectId}`);
    const result = await api.addToFavorites({
      favorited_object_id: objectId,
      favorited_object_type: objectType
    });
    console.log('✅ Ajouté aux favoris:', result);
    return result;
  } catch (error) {
    console.error('❌ Erreur lors de l\'ajout aux favoris:', error);
    
    if (error.message === 'Token expiré ou invalide' || error.status === 401) {
      removeToken(TOKEN_TYPES.CLIENT);
      throw new Error('Session expirée. Veuillez vous reconnecter.');
    }
    
    if (error.status === 409) {
      throw new Error('Cet élément est déjà dans vos favoris.');
    }
    
    throw new Error('Impossible d\'ajouter aux favoris. Veuillez réessayer.');
  }
};

/**
 * Supprime un élément des favoris
 */
export const removeFromFavorites = async (objectId, objectType) => {
  try {
    if (!isAuthenticatedForFavorites()) {
      throw new Error('Utilisateur non authentifié');
    }

    console.log(`➖ Suppression des favoris: ${objectType} ${objectId}`);
    
    // Récupérer les favoris pour trouver l'ID
    const favorites = await api.getUserFavorites();
    const favoriteToRemove = favorites.find(
      fav => fav.favorited_object_type === objectType && fav.favorited_object_id === objectId
    );
    
    if (!favoriteToRemove) {
      throw new Error('Favori non trouvé');
    }
    
    const result = await api.removeFromFavorites(favoriteToRemove.id);
    console.log('✅ Supprimé des favoris:', result);
    return result;
  } catch (error) {
    console.error('❌ Erreur lors de la suppression des favoris:', error);
    
    if (error.message === 'Token expiré ou invalide' || error.status === 401) {
      removeToken(TOKEN_TYPES.CLIENT);
      throw new Error('Session expirée. Veuillez vous reconnecter.');
    }
    
    throw new Error('Impossible de supprimer des favoris. Veuillez réessayer.');
  }
};

/**
 * Vérifie le statut de favori d'un élément
 */
export const checkFavoriteStatus = async (objectId, objectType) => {
  try {
    if (!isAuthenticatedForFavorites()) {
      return false;
    }

    const isFavorite = await api.checkFavoriteStatus(objectId, objectType);
    return isFavorite;
  } catch (error) {
    console.error('❌ Erreur lors de la vérification du statut favori:', error);
    
    // En cas d'erreur, retourner false silencieusement
    return false;
  }
};

/**
 * Bascule le statut de favori d'un élément
 */
export const toggleFavorite = async (objectId, objectType) => {
  try {
    const isFavorite = await checkFavoriteStatus(objectId, objectType);
    
    if (isFavorite) {
      await removeFromFavorites(objectId, objectType);
      return false;
    } else {
      await addToFavorites(objectId, objectType);
      return true;
    }
  } catch (error) {
    console.error('❌ Erreur lors du basculement du favori:', error);
    throw error;
  }
};

/**
 * Hook pour gérer les favoris avec état local
 */
export const useFavorites = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadFavoritesData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await loadFavorites();
      setFavorites(data);
    } catch (err) {
      setError(err.message);
      setFavorites([]);
    } finally {
      setLoading(false);
    }
  };

  const addFavorite = async (objectId, objectType) => {
    try {
      await addToFavorites(objectId, objectType);
      // Recharger les favoris
      await loadFavoritesData();
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const removeFavorite = async (objectId, objectType) => {
    try {
      await removeFromFavorites(objectId, objectType);
      // Recharger les favoris
      await loadFavoritesData();
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const toggleFavoriteStatus = async (objectId, objectType) => {
    try {
      const newStatus = await toggleFavorite(objectId, objectType);
      // Recharger les favoris
      await loadFavoritesData();
      return newStatus;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  return {
    favorites,
    loading,
    error,
    loadFavorites: loadFavoritesData,
    addFavorite,
    removeFavorite,
    toggleFavorite: toggleFavoriteStatus,
    clearError: () => setError(null)
  };
};

export default {
  isAuthenticatedForFavorites,
  loadFavorites,
  addToFavorites,
  removeFromFavorites,
  checkFavoriteStatus,
  toggleFavorite,
  useFavorites
};
